/**
 * Route prefetch registry — warms dynamic-import chunks on hover/focus
 * so navigation completes instantly when the user actually clicks.
 *
 * Pattern matchers are kept simple: longest-prefix wins.
 */

type Loader = () => Promise<unknown>;

const loaders: Array<{ test: (path: string) => boolean; load: Loader }> = [
  // Landing & marketing
  { test: (p) => p === "/", load: () => import("@/pages/SirppiLandingPage") },
  { test: (p) => p === "/career-path" || p === "/compass", load: () => import("@/pages/LandingPage") },
  { test: (p) => p === "/products", load: () => import("@/pages/ProductsPage") },
  { test: (p) => p === "/pricing", load: () => import("@/pages/PricingPage") },
  { test: (p) => p === "/company", load: () => import("@/pages/CompanyPage") },
  { test: (p) => p === "/about", load: () => import("@/pages/AboutPage") },
  { test: (p) => p === "/contact", load: () => import("@/pages/ContactPage") },

  // Core product
  { test: (p) => p === "/explore" || p.startsWith("/explore?"), load: () => import("@/pages/ExplorePage") },
  { test: (p) => p === "/quiz", load: () => import("@/pages/RoleQuizPage") },
  { test: (p) => p === "/quiz-results", load: () => import("@/pages/QuizResultsPage") },
  { test: (p) => p === "/compare", load: () => import("@/pages/CompareRolesPage") },
  { test: (p) => p === "/guided-paths", load: () => import("@/pages/GuidedPathsPage") },
  { test: (p) => p === "/career-hub", load: () => import("@/pages/CareerHubPage") },
  { test: (p) => p === "/profile", load: () => import("@/pages/ProfilePage") },
  { test: (p) => p === "/settings", load: () => import("@/pages/SettingsPage") },
  { test: (p) => p === "/notifications", load: () => import("@/pages/NotificationsPage") },

  // Studio
  { test: (p) => p === "/studio", load: () => import("@/pages/studio/StudioLandingPage") },
  { test: (p) => p === "/studio/upload", load: () => import("@/pages/studio/ResumeUploadPage") },
  { test: (p) => p === "/studio/analysis", load: () => import("@/pages/studio/ResumeAnalysisPage") },
  { test: (p) => p === "/studio/jobs", load: () => import("@/pages/studio/JobMatchingPage") },
  { test: (p) => p === "/studio/interview", load: () => import("@/pages/studio/InterviewPrepPage") },
  { test: (p) => p === "/studio/hub", load: () => import("@/pages/studio/StudioHubPage") },
];

const inflight = new Set<string>();

export const prefetchRoute = (to: string): void => {
  if (!to || to.startsWith("http") || to.startsWith("mailto:") || to.startsWith("tel:")) return;

  // Strip query/hash for matching
  const path = to.split("?")[0].split("#")[0];
  if (inflight.has(path)) return;

  const entry = loaders.find((l) => l.test(path));
  if (!entry) return;

  inflight.add(path);
  // Fire and forget — failures are silently swallowed (best-effort)
  entry.load().catch(() => inflight.delete(path));
};
